import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Chip,
  TablePagination,
} from "@mui/material";

import { Close, Save } from "@mui/icons-material";
import { useEffect, useState } from "react";
import chatwidgetService from "../../services/chatwidget.service";

const statusColors = {
  new: "primary",
  followed_up: "success",
  archived: "default",
};

const reasonOptions = [
  { value: "pricing_question", label: "Pricing Question" },
  { value: "complex_question", label: "Complex Question" },
  { value: "custom_request", label: "Custom Request" },
  { value: "product_inquiry", label: "Product Inquiry" },
  { value: "support_needed", label: "Support Needed" },
  { value: "recommendation_needed", label: "Recommendation Needed" },
  { value: "other", label: "Other" },
];

const PublicEmailLeads = ({ open, onClose }) => {
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;

  // Extract only the values we need to avoid object reference issues
  const storedUserSession =
    JSON.parse(localStorage.getItem("userSession")) || {};

  const userId = storedUserSession?.user?.userId;
  const agentId = storedUserSession?.user?.kbId;
  const leadName = storedUserSession?.user?.name;
  const leadEmail = storedUserSession?.user?.email;

  const formatReason = (v) =>
    v?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Fetch leads only when the dialog opens
  useEffect(() => {
    if (!open) return;

    const fetchLeads = async () => {
      try {
        const filterParams = { userId, agentId, leadName, leadEmail };
        const res = await chatwidgetService.searchEmailLeads(filterParams);
        setLeads(res?.data || []);
        setPage(0);
        setEditing(null);
      } catch (err) {
        console.error("Failed to load email leads:", err);
      }
    };

    fetchLeads();
  }, [open, userId, agentId, leadName, leadEmail]);

  const handleSave = async (lead) => {
    const payload = {
      question: lead.question,
      context: lead.context,
      reason: lead.reason,
      userSession: storedUserSession,
    };

    try {
      await chatwidgetService.updateEmailLead(lead.id, payload);
      // Update local state instead of refetching
      setLeads((prev) =>
        prev.map((l) => (l.id === lead.id ? { ...l, ...payload } : l))
      );
      setEditing(null);
    } catch (err) {
      console.error("Failed to update lead:", err);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Customer Emails
        <IconButton onClick={onClose} sx={{ float: "right" }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Question</TableCell>
              <TableCell>Context</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {leads.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  align="center"
                  sx={{ py: 4, color: "text.secondary" }}
                >
                  No customer emails found.
                </TableCell>
              </TableRow>
            ) : (
              leads
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((lead) => {
                  const isEdit = editing === lead.id;
                  return (
                    <TableRow key={lead.id} hover>
                      <TableCell>{lead.leadName}</TableCell>
                      <TableCell>{lead.leadEmail}</TableCell>

                      <TableCell>
                        {isEdit ? (
                          <TextField
                            fullWidth
                            value={lead.question}
                            onChange={(e) =>
                              setLeads((prev) =>
                                prev.map((l) =>
                                  l.id === lead.id
                                    ? { ...l, question: e.target.value }
                                    : l
                                )
                              )
                            }
                          />
                        ) : (
                          lead.question
                        )}
                      </TableCell>

                      <TableCell>
                        {isEdit ? (
                          <TextField
                            fullWidth
                            value={lead.context}
                            onChange={(e) =>
                              setLeads((prev) =>
                                prev.map((l) =>
                                  l.id === lead.id
                                    ? { ...l, context: e.target.value }
                                    : l
                                )
                              )
                            }
                          />
                        ) : (
                          lead.context
                        )}
                      </TableCell>

                      <TableCell>
                        {isEdit ? (
                          <Select
                            fullWidth
                            value={lead.reason}
                            onChange={(e) =>
                              setLeads((prev) =>
                                prev.map((l) =>
                                  l.id === lead.id
                                    ? { ...l, reason: e.target.value }
                                    : l
                                )
                              )
                            }
                          >
                            {reasonOptions.map((r) => (
                              <MenuItem key={r.value} value={r.value}>
                                {r.label}
                              </MenuItem>
                            ))}
                          </Select>
                        ) : (
                          formatReason(lead.reason)
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={formatReason(lead.status)}
                          color={statusColors[lead.status]}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        {isEdit ? (
                          <>
                            <Tooltip title="Save">
                              <IconButton onClick={() => handleSave(lead)}>
                                <Save />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Cancel">
                              <IconButton
                                color="error"
                                onClick={() => setEditing(null)}
                              >
                                <Close />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => setEditing(lead.id)}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>

        {leads.length > 0 && (
          <TablePagination
            component="div"
            count={leads.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[5]}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PublicEmailLeads;
